import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useApi, useConsulta } from '@plataforma/web/api';
import { TrabajoProveedor, type Trabajo } from '../componentes/TrabajoProveedor';
import { fechaCorta, fechaHoraLocal, mxn } from '../formato';
import { nombreVehiculo } from './Tablero';
import type { EntradaDirectorio } from './Directorio';
import type { Monto, Vehiculo } from './tipos';
import {
  DetalleSolicitudCompra,
  detalleSolicitudDe,
  esDeCompra,
  resumenDetalle,
} from './portal/CotizacionAduanal';

interface Solicitud {
  id: string;
  folio: string;
  titulo: string;
  especialidad: string;
  mecanica: 'cerrada' | 'abierta';
  alcance: string;
  cierraEn: string;
  estado: string;
  invitados?: number;
  detalle?: Record<string, unknown> | null;
}
interface Oferta {
  id: string;
  proveedorNombre: string;
  precio: Monto;
  plazoDias: number;
  garantiaDias: number;
  origen: 'en_linea' | 'capturada';
  estado: string;
  reputacion: { calificacion: number | null; trabajosTerminados: number } | null;
  detalle?: Record<string, unknown> | null;
}
interface DetalleSolicitud extends Solicitud {
  ofertas: Oferta[];
  trabajo: Trabajo | null;
}
interface Especialidad {
  clave: string;
  nombre: string;
}

const ESTADOS: Record<string, string> = {
  abierta: 'Abierta',
  cerrada: 'Cerrada, por adjudicar',
  adjudicada: 'Adjudicada',
  cancelada: 'Cancelada',
};

/** Solicitudes de cotización a proveedores (arquitectura 2.8, sección 9). */
export function Proveedores() {
  const api = useApi();
  const { datos, error } = useConsulta<Solicitud[]>('/workshop/proveedores/solicitudes');
  const { datos: lotero } = useConsulta<{ tipos: string[] }>('/platform/mi-lotero');
  const [aviso, setAviso] = useState<string | null>(null);
  const ofrecerServicios = async () => {
    try {
      await api('POST', '/platform/mi-lotero/rol-proveedor');
      // El menú del Portal de Proveedores sale de los tipos del lotero.
      window.location.assign('/portal/perfil');
    } catch (e) {
      setAviso((e as Error).message);
    }
  };
  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Proveedores</h1>
          <div className="subtitulo">
            Cotiza cada trabajo con la red de proveedores y adjudica la mejor oferta
          </div>
        </div>
        <div className="relleno" />
        {lotero && !lotero.tipos.includes('proveedor') && (
          <button
            className="boton"
            onClick={() => void ofrecerServicios()}
            title="Tu lotero también recibe convocatorias de otros loteros"
          >
            Ofrecer también mis servicios
          </button>
        )}
        <Link className="boton" to="/proveedores/directorio">
          Directorio
        </Link>
        <Link className="boton primario" to="/proveedores/nueva">
          Nueva solicitud
        </Link>
      </div>
      {error && <p className="error">{error}</p>}
      {aviso && <p className="error">{aviso}</p>}
      <section className="seccion">
        {datos && !datos.length ? (
          <p className="vacio">
            Sin solicitudes. Pide cotizaciones para la puerta, el módulo o la pintura que necesitas.
          </p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Solicitud</th>
                <th>Tipo</th>
                <th>Cierra</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {(datos ?? []).map((s) => (
                <tr key={s.id}>
                  <td>
                    <Link to={`/proveedores/${s.id}`}>{s.titulo}</Link>
                    <div className="vin">{s.folio}</div>
                  </td>
                  <td>{s.mecanica === 'abierta' ? 'Subasta inversa abierta' : 'Sobre cerrado'}</td>
                  <td>{fechaCorta(s.cierraEn)}</td>
                  <td>
                    <span className={`etiqueta ${s.estado === 'adjudicada' ? 'exito' : ''}`}>
                      {ESTADOS[s.estado] ?? s.estado}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

export function NuevaSolicitud() {
  const api = useApi();
  const navegar = useNavigate();
  const { datos: especialidades } = useConsulta<Especialidad[]>('/workshop/especialidades');
  const { datos: vehiculos } = useConsulta<Vehiculo[]>('/inventory/vehiculos');
  const [params] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [especialidad, setEspecialidad] = useState('');
  const [vehiculoId, setVehiculoId] = useState(params.get('vehiculo') ?? '');
  const enDosDias = fechaHoraLocal(new Date(Date.now() + 2 * 86_400_000));
  const enviar = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setEnviando(true);
    setError(null);
    const vehiculo = (vehiculos ?? []).find((v) => v.id === f.get('vehiculoId'));
    try {
      const s = await api<Solicitud>('POST', '/workshop/proveedores/solicitudes', {
        ...(vehiculo && {
          vehiculoId: vehiculo.id,
          vin: vehiculo.vin,
          vehiculo: { anio: vehiculo.anio, marca: vehiculo.marca, modelo: vehiculo.modelo },
        }),
        titulo: f.get('titulo'),
        especialidad: f.get('especialidad'),
        necesidades: [{ tipo: f.get('tipo'), descripcion: f.get('descripcion') }],
        estadoMx: f.get('estadoMx'),
        ciudad: f.get('ciudad'),
        mecanica: f.get('mecanica'),
        alcance: f.get('alcance'),
        cierraEn: new Date(String(f.get('cierraEn'))).toISOString(),
        ...(esDeCompra(String(f.get('especialidad'))) && {
          detalle: detalleSolicitudDe(String(f.get('especialidad')), f),
        }),
      });
      void navegar(`/proveedores/${s.id}`);
    } catch (err) {
      setError((err as Error).message);
      setEnviando(false);
    }
  };
  return (
    <>
      <h1>Nueva solicitud de cotización</h1>
      <section className="seccion">
        <form className="cuerpo formulario" onSubmit={(e) => void enviar(e)}>
          <label className="campo">
            <span>Qué necesitas</span>
            <input
              name="titulo"
              required
              maxLength={140}
              placeholder="Puerta delantera izquierda"
            />
          </label>
          <label className="campo">
            <span>Vehículo (su costo llega a la bitácora)</span>
            <select
              name="vehiculoId"
              value={vehiculoId}
              onChange={(e) => setVehiculoId(e.target.value)}
            >
              <option value="">Sin vehículo</option>
              {(vehiculos ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  {nombreVehiculo(v)} · {v.vin.slice(-6)}
                </option>
              ))}
            </select>
          </label>
          <label className="campo">
            <span>Especialidad</span>
            <select
              name="especialidad"
              required
              value={especialidad || (especialidades?.[0]?.clave ?? '')}
              onChange={(e) => setEspecialidad(e.target.value)}
            >
              {(especialidades ?? []).map((e) => (
                <option key={e.clave} value={e.clave}>
                  {e.nombre}
                </option>
              ))}
            </select>
          </label>
          {esDeCompra(especialidad) && (
            <DetalleSolicitudCompra
              especialidad={especialidad}
              vehiculo={(vehiculos ?? []).find((v) => v.id === vehiculoId) ?? null}
            />
          )}
          <label className="campo">
            <span>Trabajo</span>
            <select name="tipo" defaultValue="reparar">
              <option value="reparar">Reparar</option>
              <option value="reemplazar">Reemplazar (pieza e instalación)</option>
              <option value="suministrar">Solo la pieza</option>
            </select>
          </label>
          <label className="campo" style={{ gridColumn: '1 / -1' }}>
            <span>Descripción</span>
            <textarea name="descripcion" required rows={3} maxLength={1000} />
          </label>
          <label className="campo">
            <span>Estado</span>
            <input name="estadoMx" required maxLength={3} defaultValue="JAL" />
          </label>
          <label className="campo">
            <span>Ciudad</span>
            <input name="ciudad" required maxLength={80} />
          </label>
          <label className="campo">
            <span>Tipo de subasta</span>
            <select name="mecanica" defaultValue="cerrada">
              <option value="cerrada">Sobre cerrado (nadie ve ofertas ajenas)</option>
              <option value="abierta">Subasta inversa abierta (ven el mejor precio)</option>
            </select>
          </label>
          <label className="campo">
            <span>A quién</span>
            <select name="alcance" defaultValue="ambos">
              <option value="ambos">Mis proveedores de confianza y la red</option>
              <option value="confianza">Solo mis proveedores de confianza</option>
              <option value="red">La red de la especialidad en mi estado</option>
              <option value="directorio">Nadie en línea (capturo cotizaciones)</option>
            </select>
          </label>
          <label className="campo">
            <span>Cierra</span>
            <input name="cierraEn" type="datetime-local" required defaultValue={enDosDias} />
          </label>
          {error && (
            <p className="error" style={{ gridColumn: '1 / -1' }}>
              {error}
            </p>
          )}
          <div className="acciones">
            <Link className="boton" to="/proveedores">
              Cancelar
            </Link>
            <button className="boton primario" disabled={enviando}>
              {enviando ? 'Enviando…' : 'Pedir cotizaciones'}
            </button>
          </div>
        </form>
      </section>
    </>
  );
}

export function SolicitudDetalle() {
  const { id } = useParams();
  const api = useApi();
  const {
    datos: s,
    error,
    recargar,
  } = useConsulta<DetalleSolicitud>(`/workshop/proveedores/solicitudes/${id}`);
  const [aviso, setAviso] = useState<string | null>(null);
  const adjudicar = async (ofertaId: string) => {
    setAviso(null);
    try {
      await api('POST', `/workshop/proveedores/solicitudes/${id}/adjudicar`, { ofertaId });
      recargar();
    } catch (e) {
      setAviso((e as Error).message);
    }
  };
  if (error) return <p className="error">{error}</p>;
  if (!s) return <p className="subtitulo">Cargando…</p>;
  return (
    <>
      <div className="encabezado">
        <div>
          <div className="subtitulo">
            <Link to="/proveedores">Proveedores</Link> › {s.folio}
          </div>
          <h1>{s.titulo}</h1>
          <div className="subtitulo">
            {s.mecanica === 'abierta' ? 'Subasta inversa abierta' : 'Sobre cerrado'} ·{' '}
            {s.invitados ?? 0} proveedores invitados · cierra {fechaCorta(s.cierraEn)}
            {s.detalle && esDeCompra(s.especialidad)
              ? ` · ${resumenDetalle(s.especialidad, s.detalle)}`
              : ''}
          </div>
        </div>
        <div className="relleno" />
        <span className="etiqueta">{ESTADOS[s.estado] ?? s.estado}</span>
      </div>
      {aviso && <p className="error">{aviso}</p>}
      {s.trabajo && <TrabajoProveedor t={s.trabajo} alCambiar={recargar} />}
      <section className="seccion">
        <header>
          <h2>Ofertas</h2>
        </header>
        {!s.ofertas.length ? (
          <p className="vacio">Todavía no llegan ofertas.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Proveedor</th>
                <th className="num">Precio</th>
                <th className="num">Plazo</th>
                <th className="num">Garantía</th>
                <th>Reputación</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {s.ofertas.map((o) => (
                <tr key={o.id}>
                  <td>
                    {o.proveedorNombre}{' '}
                    {o.origen === 'capturada' && <span className="etiqueta">capturada</span>}
                  </td>
                  <td className="num">
                    {mxn(o.precio.equivalenteMxn)}
                    {o.detalle && esDeCompra(s.especialidad) && (
                      <div className="nota">{resumenDetalle(s.especialidad, o.detalle)}</div>
                    )}
                  </td>
                  <td className="num">{o.plazoDias} días</td>
                  <td className="num">{o.garantiaDias} días</td>
                  <td>
                    {o.reputacion?.calificacion
                      ? `${o.reputacion.calificacion} ★ · ${o.reputacion.trabajosTerminados} trabajos`
                      : 'Sin calificaciones'}
                  </td>
                  <td className="num">
                    {s.estado === 'abierta' || s.estado === 'cerrada' ? (
                      o.estado === 'vigente' && (
                        <button className="boton" onClick={() => void adjudicar(o.id)}>
                          Adjudicar
                        </button>
                      )
                    ) : (
                      <span className={`etiqueta ${o.estado === 'ganada' ? 'exito' : ''}`}>
                        {o.estado.replace('_', ' ')}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      {(s.estado === 'abierta' || s.estado === 'cerrada') && (
        <CapturarOferta solicitudId={s.id} alGuardar={recargar} />
      )}
    </>
  );
}

/** Cotización que llegó por fuera (teléfono, WhatsApp) de un proveedor del directorio. */
export function CapturarOferta({
  solicitudId,
  alGuardar,
}: {
  solicitudId: string;
  alGuardar: () => void;
}) {
  const api = useApi();
  const { datos: directorio } = useConsulta<EntradaDirectorio[]>(
    '/workshop/proveedores/directorio',
  );
  const [error, setError] = useState<string | null>(null);
  const externos = (directorio ?? []).filter((d) => !d.proveedorId);
  const enviar = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const forma = e.currentTarget;
    const f = new FormData(forma);
    setError(null);
    try {
      await api('POST', `/workshop/proveedores/solicitudes/${solicitudId}/ofertas`, {
        directorioId: f.get('directorioId'),
        precio: {
          cantidad: String(f.get('cantidad')),
          moneda: 'MXN',
          fecha: fechaHoraLocal(new Date()).slice(0, 10),
        },
        plazoDias: Number(f.get('plazoDias')),
        garantiaDias: Number(f.get('garantiaDias') || 0),
      });
      forma.reset();
      alGuardar();
    } catch (err) {
      setError((err as Error).message);
    }
  };
  return (
    <section className="seccion">
      <header>
        <h2>Capturar cotización recibida por fuera</h2>
      </header>
      {!externos.length ? (
        <p className="vacio">
          Agrega primero al proveedor externo en tu{' '}
          <Link to="/proveedores/directorio">directorio</Link>.
        </p>
      ) : (
        <form className="cuerpo formulario" onSubmit={(e) => void enviar(e)}>
          <label className="campo">
            <span>Proveedor</span>
            <select name="directorioId" required>
              {externos.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.nombre}
                </option>
              ))}
            </select>
          </label>
          <label className="campo">
            <span>Precio (MXN)</span>
            <input name="cantidad" required inputMode="decimal" pattern="\d{1,12}(\.\d{1,2})?" />
          </label>
          <label className="campo">
            <span>Plazo (días)</span>
            <input name="plazoDias" type="number" min={0} max={365} required defaultValue={3} />
          </label>
          <label className="campo">
            <span>Garantía (días)</span>
            <input name="garantiaDias" type="number" min={0} max={3650} defaultValue={0} />
          </label>
          {error && (
            <p className="error" style={{ gridColumn: '1 / -1' }}>
              {error}
            </p>
          )}
          <div className="acciones">
            <button className="boton">Capturar cotización</button>
          </div>
        </form>
      )}
    </section>
  );
}
